import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/lib/translate";
import React, { useEffect, useState } from "react";
import { Textarea } from "../ui/textarea";
import { useAppStore } from "@/hooks/useAppStore";

type ConversationContext = {
  code: string;
  name: string;
};

interface ConversationContextDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ConversationContextDialog: React.FC<
  ConversationContextDialogProps
> = ({ open, onOpenChange }) => {
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const conversationSetting = useAppStore((state) => state.conversationSetting);
  const setConversationContextSetting = useAppStore((state) => state.setConversationContextSetting);
  const t = useTranslation(preferredLanguage);

  const CONTEXT_CONVERSATION_TYPES = [
    { code: "", name: t("ConversationContext_none") },
    { code: "tourism", name: t("ConversationContext_tourism") },
    { code: "work", name: t("ConversationContext_work") },
    { code: "casual", name: t("ConversationContext_casual") },
    { code: "dating", name: t("ConversationContext_dating") },
    { code: "customize", name: t("ConversationContext_customize") },
  ];

  const [error, setError] = useState("");
  const [contextConversationTypeSelected, setContextConversationTypeSelected] = useState<ConversationContext>(CONTEXT_CONVERSATION_TYPES[0]);
  const [customContextInput, setCustomContextInput] = useState("");

  useEffect(() => {
    const selectedConversationContext = conversationSetting.presetOption
      ? CONTEXT_CONVERSATION_TYPES.find(
          (type) => type.code === conversationSetting.presetOption
        )
      : CONTEXT_CONVERSATION_TYPES[0];

    setContextConversationTypeSelected(
      selectedConversationContext || CONTEXT_CONVERSATION_TYPES[0]
    );
    setCustomContextInput(conversationSetting.contextInput);
  }, [conversationSetting]);

  useEffect(() => {
    // Clear state when dialog opens or closes
    if (!open) {
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");
    if (
      contextConversationTypeSelected.code === "customize" &&
      customContextInput.trim() === ""
    ) {
      setError(t("Description is required when 'Customize' is selected"));
      return;
    }

    // Save the conversation context setting to the global state or backend as needed
    // For example:
    await setConversationContextSetting({
      contextInput:
        contextConversationTypeSelected.code === "customize"
          ? customContextInput.trim()
          : "",
      presetOption: contextConversationTypeSelected.code,
    });

    // Close the dialog after submission

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby="comment-translation-dialog"
        className="sm:max-w-[425px] bg-neutral-800 border-neutral-700 text-white"
      >
        <DialogHeader>
          <DialogTitle>{t("Conversation context")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type-evaluation" className="text-right ">
                {t("Preset")} *
              </Label>
              <div className="relative inline-block text-left">
                <select
                  value={contextConversationTypeSelected.code} // Keep track of selected item
                  onChange={(e) => {
                    const selectedType = CONTEXT_CONVERSATION_TYPES.find(
                      (type) => type.code === e.target.value
                    );
                    if (selectedType) {
                      setContextConversationTypeSelected(selectedType);
                    }
                  }}
                  className="flex items-center gap-2 border border-neutral-500/40 dark:border-neutral px-3 py-1 text-xs text-neutral-800 dark:text-neutral-200 bg-neutral-100 dark:bg-[#0f1f23] w-40"
                >
                  <option
                    value=""
                    disabled
                    className="text-neutral-500 dark:text-neutral-400"
                  >
                    {t("Select Type")}
                  </option>
                  {CONTEXT_CONVERSATION_TYPES.map(
                    (type: ConversationContext) => (
                      <option
                        key={type.code}
                        value={type.code}
                        className="text-neutral-800 dark:text-neutral-200"
                        selected={
                          type.code === contextConversationTypeSelected.code
                        }
                      >
                        {type.name}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
            {contextConversationTypeSelected.code === "customize" && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="translation-evaluation" className="text-right">
                  {t("Context")} *
                </Label>
                <Textarea
                  id="translation-evaluation"
                  value={customContextInput || ""}
                  onChange={(e) => setCustomContextInput(e.target.value)}
                  className="col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-chotto-gold"
                  placeholder={t("Enter your comment")}
                />
              </div>
            )}
            {error && (
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="text-red-400 text-sm text-center col-span-4">
                  {error}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              className="bg-chotto-share-blue hover:bg-chotto-share-blue/90"
            >
              {t("Submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
